import { computed, type Ref } from 'vue';
import { useQuery, useMutation } from '@tanstack/vue-query';
import { useApi, useToaster } from '@shopexperts/shared';
import type { ValidateInviteResponse, AcceptInviteResponse } from '@shopexperts/shared';

export interface AcceptInvitePayload {
  token: string;
  password: string;
  password_confirmation: string;
}

/**
 * Composable for validating and accepting user invitations.
 * 
 * This handles the invite acceptance flow:
 * 1. Validates the token from the URL to get invite details
 * 2. Allows the user to set a password and accept the invite
 * 3. Returns the auth token for immediate login
 * 
 * @param token - The invitation token from the URL query parameter
 * 
 * @example
 * ```ts
 * const route = useRoute();
 * const token = computed(() => route.query.token as string);
 * 
 * const { 
 *   inviteData, 
 *   isValidating, 
 *   validationError,
 *   accept, 
 *   isAccepting 
 * } = useAcceptInvite(token);
 * 
 * // Show form once validated
 * if (inviteData.value) {
 *   console.log('Inviting:', inviteData.value.email);
 * }
 * 
 * // Accept the invite
 * const result = await accept({
 *   password: 'SecurePassword123!',
 *   password_confirmation: 'SecurePassword123!'
 * });
 * ```
 */
export function useAcceptInvite(token: Ref<string | undefined>) {
  const api = useApi();
  const toaster = useToaster();

  // Only run validation if we have a token
  const hasToken = computed(() => !!token.value);

  // ==================
  // Validate Token Query
  // ==================
  const {
    data: inviteData,
    isLoading: isValidating,
    isError: isValidationError,
    error: validationError,
    refetch: revalidate,
  } = useQuery({
    queryKey: ['invites', 'validate', token],
    queryFn: async (): Promise<ValidateInviteResponse> => {
      if (!token.value) {
        throw new Error('No invitation token provided');
      }
      return await api.public.invites.validate({
        params: { token: token.value },
      });
    },
    enabled: hasToken,
    staleTime: Infinity, // Token validation doesn't change
    retry: false, // Don't retry on validation failures
  });

  // ==================
  // Accept Invite Mutation
  // ==================
  const acceptMutation = useMutation({
    mutationKey: ['invites', 'accept'],
    mutationFn: async (payload: Omit<AcceptInvitePayload, 'token'>): Promise<AcceptInviteResponse> => {
      if (!token.value) {
        throw new Error('No invitation token provided');
      }
      return await api.public.invites.accept({
        body: {
          token: token.value,
          password: payload.password,
          password_confirmation: payload.password_confirmation,
        },
      });
    },
    onError: (error: any) => {
      console.error('[useAcceptInvite] Accept error:', error);
      
      const errorMessage = 
        error?.response?.data?.message || 
        error?.message ||
        'Failed to set password. Please try again.';
      
      toaster.create({
        title: 'Error',
        description: errorMessage,
        type: 'error',
      });
    },
  });

  const isAccepting = computed(() => acceptMutation.isPending.value);
  const isAcceptSuccess = computed(() => acceptMutation.isSuccess.value);
  const isAcceptError = computed(() => acceptMutation.isError.value);
  const acceptError = computed(() => acceptMutation.error.value);
  const acceptData = computed(() => acceptMutation.data.value);

  /**
   * Accept the invitation with the provided password
   */
  const accept = (payload: Omit<AcceptInvitePayload, 'token'>) => {
    return acceptMutation.mutateAsync(payload);
  };

  /**
   * Reset the accept mutation state (useful for retry scenarios)
   */
  const resetAccept = () => {
    acceptMutation.reset();
  };

  return {
    // Validation state
    inviteData,
    isValidating,
    isValidationError,
    validationError,
    revalidate,

    // Accept state
    accept,
    isAccepting,
    isAcceptSuccess,
    isAcceptError,
    acceptError,
    acceptData,
    resetAccept,
  };
}
